import React, { useState, useEffect } from 'react';
import styles from '../../styles/RolesPermissionsStyles/RolesPermissions.module.css';
import { getPermisos, savePermisosBulk, getCampusForRoles } from '../../api/roles';

function PermissionsTable({ roleId, onPermissionsSaved }) {
  const [permissions, setPermissions] = useState({});
  const [campusList, setCampusList] = useState([]);
  const [selectedCampus, setSelectedCampus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Módulos del sistema - Nueva estructura del sidebar
  const modules = [
    { key: 'Consulta', name: 'Consulta' },
    { key: 'Finanzas', name: 'Finanzas' },
    { key: 'Control Escolar', name: 'Control Escolar' },
    { key: 'Perfil Académico', name: 'Perfil Académico' },
    { key: 'Administración del Sistema', name: 'Administración del Sistema' },
    { key: 'Admisiones', name: 'Admisiones' },
    { key: 'Otros', name: 'Otros' },
    { key: 'Panel Asesor', name: 'Panel Asesor' }
  ];

  // Cargar permisos y campus cuando cambia el rol
  useEffect(() => {
    if (roleId) {
      loadPermisos();
      loadCampus();
    }
  }, [roleId]);

  const loadPermisos = async () => {
    try {
      setLoading(true);
      const data = await getPermisos({ id_rol: roleId });
      
      // Mapear permisos a estructura del componente
      const permisosMap = {};
      data.forEach(permiso => {
        permisosMap[permiso.modulo] = {
          puede_crear: permiso.puede_crear,
          puede_leer: permiso.puede_leer,
          puede_actualizar: permiso.puede_actualizar,
          puede_eliminar: permiso.puede_eliminar
        };
      });
      
      setPermissions(permisosMap);
    } catch (error) {
      console.error('Error al cargar permisos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampus = async () => {
    try {
      const data = await getCampusForRoles();
      setCampusList(data);
    } catch (error) {
      console.error('Error al cargar campus:', error);
    }
  };

  const handlePermissionChange = (module, permission) => {
    setPermissions(prev => ({
      ...prev,
      [module]: {
        ...(prev[module] || {}),
        [permission]: !(prev[module]?.[permission] || false)
      }
    }));
  };

  const handleCampusChange = (campusId) => {
    setSelectedCampus(prev => {
      if (prev.includes(campusId)) {
        return prev.filter(id => id !== campusId);
      } else {
        return [...prev, campusId];
      }
    });
  };

  const handleSavePermissions = async () => {
    try {
      setSaving(true);
      
      // Convertir permisos a formato del backend
      const permisos = modules.map(module => ({
        modulo: module.key,
        puede_crear: permissions[module.key]?.puede_crear || false,
        puede_leer: permissions[module.key]?.puede_leer || false,
        puede_actualizar: permissions[module.key]?.puede_actualizar || false,
        puede_eliminar: permissions[module.key]?.puede_eliminar || false,
        puede_aprobar: false
      }));
      
      await savePermisosBulk({
        id_rol: roleId,
        permisos: permisos,
        campus_ids: selectedCampus
      });
      
      alert('Permisos guardados exitosamente');
      
      if (onPermissionsSaved) {
        onPermissionsSaved();
      }
    } catch (error) {
      console.error('Error al guardar permisos:', error);
      alert('Error al guardar permisos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.permissionsContainer}>
        <p style={{ textAlign: 'center', padding: '20px' }}>Cargando permisos...</p>
      </div>
    );
  }

  return (
    <div className={styles.permissionsContainer}>
      <div className={styles.permissionsHeader}>
        <h3 className={styles.permissionsTitle}>Permisos por módulo</h3>
      </div>
      
      <div className={styles.permissionsContent}>
        <div className={styles.permissionsGrid}>
          {/* Headers - Crear, Ver, Editar, Eliminar */}
          <div className={styles.permissionsRow} style={{ gridTemplateColumns: '250px repeat(4, 100px)' }}>
            <div className={styles.moduleColumn}>
              <span className={styles.columnHeader}>MÓDULO</span>
            </div>
            <div className={styles.permissionColumn}>
              <span className={styles.columnHeader}>Crear</span>
            </div>
            <div className={styles.permissionColumn}>
              <span className={styles.columnHeader}>Ver</span>
            </div>
            <div className={styles.permissionColumn}>
              <span className={styles.columnHeader}>Editar</span>
            </div>
            <div className={styles.permissionColumn}>
              <span className={styles.columnHeader}>Eliminar</span>
            </div>
          </div>
          
          {/* Module Rows */}
          {modules.map(module => (
            <div 
              key={module.key} 
              className={styles.permissionsRow}
              style={{ gridTemplateColumns: '250px repeat(4, 100px)' }}
            >
              <div className={styles.moduleColumn}>
                <span className={styles.moduleName}>{module.name}</span>
              </div>
              
              {/* Crear */}
              <div className={styles.permissionColumn}>
                <label className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={permissions[module.key]?.puede_crear || false}
                    onChange={() => handlePermissionChange(module.key, 'puede_crear')}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkmark}></span>
                </label>
              </div>
              
              {/* Ver */}
              <div className={styles.permissionColumn}>
                <label className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={permissions[module.key]?.puede_leer || false}
                    onChange={() => handlePermissionChange(module.key, 'puede_leer')}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkmark}></span>
                </label>
              </div>
              
              {/* Editar */}
              <div className={styles.permissionColumn}>
                <label className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={permissions[module.key]?.puede_actualizar || false}
                    onChange={() => handlePermissionChange(module.key, 'puede_actualizar')}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkmark}></span>
                </label>
              </div>
              
              {/* Eliminar */}
              <div className={styles.permissionColumn}>
                <label className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={permissions[module.key]?.puede_eliminar || false}
                    onChange={() => handlePermissionChange(module.key, 'puede_eliminar')}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkmark}></span>
                </label>
              </div>
            </div>
          ))}
        </div>
        
        {/* Campus Section - Conectado a tabla real */}
        <div className={styles.campusSection}>
          <h4 className={styles.sectionTitle}>Campus</h4>
          <div className={styles.campusList}>
            {campusList.length > 0 ? (
              campusList.map((campus, index) => (
                <label key={`campus-${campus.id}-${index}`} className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={selectedCampus.includes(campus.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCampusChange(campus.id);
                    }}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkmark}></span>
                  <span className={styles.checkboxLabel}>{campus.nombre}</span>
                </label>
              ))
            ) : (
              <p style={{ color: '#898d8d', fontSize: '14px' }}>No hay campus disponibles</p>
            )}
          </div>
        </div>

        {/* Botón Guardar Permisos - Siempre visible */}
        <div className={styles.permissionsSaveSection + ' ' + styles.stickySaveSection}>
          <button 
            className={styles.saveButton}
            onClick={handleSavePermissions}
            disabled={saving}
          >
            {saving ? 'GUARDANDO...' : 'GUARDAR PERMISOS'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PermissionsTable;
